import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, GridItem, HStack, SimpleGrid, Skeleton, Stack, Text, useBreakpointValue, } from '@chakra-ui/react';
import { CheckoutContactForm } from '@client/components/checkout/CheckoutContactForm';
import { CheckoutHeading } from '@client/components/checkout/CheckoutHeading';
import { CHECKOUT_BUTTON_MARGIN_BOTTOM, CHECKOUT_FIELD_GAP, CHECKOUT_GAP, SUMMARY_ROW_GAP, SUMMARY_ROW_HEIGHT, SUMMARY_TOTAL_GAP, } from '@client/components/checkout/checkoutLayout';
import { CheckoutPaymentForm } from '@client/components/checkout/CheckoutPaymentForm';
import { CheckoutShippingForm } from '@client/components/checkout/CheckoutShippingForm';
import { CheckoutShoppingCart } from '@client/components/checkout/CheckoutShoppingCart';
import { CheckoutShoppingCartCompact } from '@client/components/checkout/CheckoutShoppingCartCompact';
import { ShoppingCartEmptyMessage } from '@client/components/shoppingCart/ShoppingCartEmptyMessage';
import { ShoppingCartSummary } from '@client/components/shoppingCart/ShoppingCartSummary';
import { SectionTitle } from '@client/components/textDisplay/SectionTitle';
import { Layout } from '@client/constants';
import { simplifyCartItems } from '@client/domain/checkout';
import { useApiClient } from '@client/hooks/useApiClient';
import { useOrderStatusPoll } from '@client/hooks/useOrderStatusPoll';
import { CheckoutType, useCheckoutStatus, } from '@client/providers/CheckoutStatusProvider';
import { useShoppingCart } from '@client/providers/ShoppingCartProvider';
import { toastError } from '@client/toaster';
import { callApi } from '@client/utils/apiUtils';
import { getOrderPath } from '@client/utils/orderUtils';
import { formatCentsAsDollars } from '@heirloom/common/utils/priceDisplay';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useEffect, useRef } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { RxDotFilled } from 'react-icons/rx';
import { useNavigate } from 'react-router-dom';
const SummarySkeleton = () => (_jsxs(Stack, { gap: SUMMARY_ROW_GAP, children: [_jsx(Skeleton, { height: SUMMARY_ROW_HEIGHT }), _jsx(Skeleton, { height: SUMMARY_ROW_HEIGHT }), _jsx(Skeleton, { height: SUMMARY_ROW_HEIGHT })] }));
export const CheckoutPage = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth0();
    const stripe = useStripe();
    const elements = useElements();
    const apiClient = useApiClient();
    const { pollUntilPaid } = useOrderStatusPoll();
    const { items, itemQuantityTotal, itemPriceTotal, shippingTotal, taxTotal, taxCalcLoading, cartLoading, checkoutEmail, shippingAddress, validateLocalCheckoutFields, validateAddressDeliverable, hydrateCart, } = useShoppingCart();
    const { pendingCheckout, pendingSubmission, startSubmitting, startConfirming, settle, triggerTimeout, } = useCheckoutStatus();
    const shippingFormRef = useRef(null);
    useEffect(() => {
        hydrateCart();
    }, []);
    const orderTotal = itemPriceTotal + shippingTotal + (taxTotal || 0);
    const handleConfirmation = async () => {
        const scrollToShippingForm = () => {
            if (layout === Layout.MOBILE && shippingFormRef.current) {
                const top = shippingFormRef.current.getBoundingClientRect()
                    .top;
                window.scrollTo({ top, behavior: 'smooth' });
            }
        };
        if (!validateLocalCheckoutFields()) {
            scrollToShippingForm();
            return;
        }
        if (!stripe || !elements)
            return;
        startSubmitting(CheckoutType.StandardCheckout);
        if (!(await validateAddressDeliverable())) {
            settle();
            scrollToShippingForm();
            return;
        }
        const { error } = await elements.submit();
        if (error) {
            settle();
            toastError('Invalid payment details', error.message ??
                'Please check your payment details and try again.');
            return;
        }
        const intentResult = await callApi(apiClient.checkout.submitOrder({
            body: {
                items: simplifyCartItems(items),
                email: checkoutEmail,
                shippingAddress,
                totalCents: orderTotal,
            },
        }));
        if (intentResult.status === 409) {
            settle();
            toastError('Prices have changed', 'Please refresh the page and try again.');
            return;
        }
        if (intentResult.error !== null) {
            settle();
            toastError('Failed to submit order', intentResult.error);
            return;
        }
        const { clientSecret, orderShortId, accessKey } = intentResult.data;
        const { error: confirmError } = await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: {
                return_url: `${window.location.origin}${getOrderPath(orderShortId, accessKey, isAuthenticated)}`,
            },
            redirect: 'if_required',
        });
        if (confirmError) {
            settle();
            toastError('Payment failed', confirmError.message ??
                'Please check your payment details and try again.');
        }
        else {
            startConfirming();
            pollUntilPaid(orderShortId, accessKey, {
                onSuccess: settle,
                onTimeout: triggerTimeout,
            });
        }
    };
    const layout = useBreakpointValue({
        base: Layout.MOBILE,
        md: Layout.DESKTOP,
    });
    if (itemQuantityTotal === 0) {
        return (_jsx(Box, { position: "absolute", top: 0, bottom: 0, left: 0, right: 0, children: _jsx(ShoppingCartEmptyMessage, { onClick: () => navigate('/') }) }));
    }
    if (layout === Layout.MOBILE) {
        return (_jsxs(Stack, { gap: 0, children: [_jsxs(Stack, { p: CHECKOUT_GAP, gap: CHECKOUT_GAP, ref: shippingFormRef, children: [_jsx(SectionTitle, { children: "Complete Your Order" }), _jsx(CheckoutContactForm, { disabled: pendingCheckout }), _jsx(CheckoutShippingForm, { layout: layout, disabled: pendingCheckout }), _jsx(CheckoutShoppingCartCompact, {})] }), _jsxs(Stack, { p: CHECKOUT_GAP, gap: CHECKOUT_GAP, background: "brand", children: [_jsxs(Stack, { gap: SUMMARY_TOTAL_GAP, children: [cartLoading ? (_jsxs(_Fragment, { children: [_jsx(SummarySkeleton, {}), _jsx(Skeleton, { height: 50 })] })) : (_jsxs(_Fragment, { children: [_jsx(ShoppingCartSummary, { textColor: 'white', pendingMessage: "Enter shipping address" }), _jsx(Text, { fontSize: 32, fontWeight: 600, color: "white", children: formatCentsAsDollars(orderTotal) })] })), _jsx(CheckoutPaymentForm, { disabled: pendingCheckout })] }), cartLoading ? (_jsx(Skeleton, { height: 16 })) : (_jsxs(Button, { size: "2xl", width: "full", fontSize: 24, variant: "outline", color: "white", border: "2px solid white", onClick: handleConfirmation, disabled: pendingCheckout || taxCalcLoading, loading: pendingSubmission ===
                                CheckoutType.StandardCheckout, children: [_jsx(FaCheckCircle, {}), "Pay Now"] }))] })] }));
    }
    return (_jsxs(Stack, { gap: CHECKOUT_GAP, my: 7, px: CHECKOUT_GAP, mx: "auto", maxWidth: { lg: 1000 }, children: [_jsx(SectionTitle, { children: "Complete Your Order" }), _jsxs(SimpleGrid, { columns: { md: 5, lg: 3 }, gapX: 10, gapY: CHECKOUT_GAP, children: [_jsx(GridItem, { colSpan: { md: 3, lg: 2 }, children: _jsxs(Stack, { gap: 7, children: [_jsx(CheckoutContactForm, { disabled: pendingCheckout }), _jsx(CheckoutShippingForm, { layout: layout, disabled: pendingCheckout }), _jsxs(Stack, { gap: CHECKOUT_FIELD_GAP, children: [_jsx(CheckoutHeading, { children: "Payment" }), _jsx(CheckoutPaymentForm, { disabled: pendingCheckout })] })] }) }), _jsx(GridItem, { colSpan: { md: 2, lg: 1 }, children: _jsxs(Stack, { gap: CHECKOUT_GAP, children: [_jsx(CheckoutShoppingCart, {}), cartLoading ? (_jsxs(_Fragment, { children: [_jsx(SummarySkeleton, {}), _jsx(Skeleton, { height: 50, mb: CHECKOUT_BUTTON_MARGIN_BOTTOM })] })) : (_jsxs(_Fragment, { children: [_jsx(ShoppingCartSummary, { pendingMessage: "Enter shipping address" }), _jsxs(Button, { size: "xl", width: "100%", mb: CHECKOUT_BUTTON_MARGIN_BOTTOM, fontSize: 24, onClick: handleConfirmation, disabled: pendingCheckout ||
                                                taxCalcLoading, loading: pendingSubmission ===
                                                CheckoutType.StandardCheckout, children: [_jsxs(HStack, { gap: 3, children: [_jsx(FaCheckCircle, {}), "Pay Now"] }), _jsx(RxDotFilled, {}), _jsx(Text, { fontSize: 28, fontWeight: 600, paddingBottom: 0.5, children: formatCentsAsDollars(orderTotal) })] })] }))] }) })] })] }));
};
